import { IconProp } from '@fortawesome/fontawesome-svg-core';

import { IUserResume } from './user.interface';
import { IWish } from './wish.interface';

export enum EGroupStateKeys {
  groupList = 'groupList',
  groupComplete = 'groupComplete',
  groupInfo = 'groupInfo',
  groupMembers = 'groupMembers',
  groupWishes = 'groupWishes',
  groupMoneybox = 'groupMoneybox',
}

export type TGroupStateKeys =
  | EGroupStateKeys.groupList
  | EGroupStateKeys.groupComplete
  | EGroupStateKeys.groupInfo
  | EGroupStateKeys.groupMembers
  | EGroupStateKeys.groupWishes
  | EGroupStateKeys.groupMoneybox;

export interface IGroupState {
  [EGroupStateKeys.groupList]: any;
  [EGroupStateKeys.groupComplete]: any;
  [EGroupStateKeys.groupInfo]: any;
  [EGroupStateKeys.groupMembers]: any;
  [EGroupStateKeys.groupWishes]: any;
  [EGroupStateKeys.groupMoneybox]: any;
}

export enum ERefound {
  TO_USER = 0,
  TO_ADMIN = 1,
  TO_ONG = 2,
}

export type TRefound = ERefound.TO_USER | ERefound.TO_ADMIN | ERefound.TO_ONG;

export enum EFeeType {
  FIXED = 0,
  VARIABLE = 1,
  UNDEFINED = 2,
  HIDDEN = 3
}

export type TFeeType = EFeeType;

export interface IGroup {
  id: string;
  name: string;
  photoUrl: string;
  spentAmount: number;
  raisedAmount: number;
  externalAmount: number;
  excessAmount: number;
  refundAmount: number;
  donationAmount: number;
  amountReturnType: ERefound;
  payLimitDate: string;
  buyLimitDate: string;
  deliveryLimitDate: string;
  amount: number;
  wishPurchased: IWish;
  isUsedMoneybox?: boolean;
  firstPayment: boolean;
  admin: IGroupWizardMember;
  members: IGroupWizardMember[];
  wishes: IWish[];
  feeType: TFeeType;
}

export interface IGroupResume {
  id: string;
  name: string;
  isCurrentUserAdmin: boolean;
  buyLimitDate: string;
  payLimitDate: string;
  wishPurchased?: IWish;
  isUsedMoneybox?: boolean;
  firstPayment?: boolean;
  hasPendingPayments: boolean;
  paid: boolean;
  photoUrl?: string;
}

export interface IActiveGroupConfig {
  name: string;
  icon?: IconProp;
  image?: any;
  Component: (...args: any) => React.ReactElement;
  props?:
    | IActiveGroupInfoProps
    | IActiveGroupMembersProps
    | IActiveGroupWishesProps
    | IActiveGroupMoneyboxProps;
}

export interface IActiveGroupInfoProps {
  groupId: string;
}

export interface IActiveGroupMembersProps {
  admin: IUserResume;
  currentUserId: string;
  memberList: IGroupMember[];
}

export interface IActiveGroupWishesProps {
  groupId: string;
  buyLimitDate: Date;
  deliveryLimitDate: Date;
  wishesList: IGroupWish[];
  currentUserId: string;
  admin: {
    id: string;
    name: string;
    nickname: string;
    userPhoto: string;
    amountPaid: number;
    hasPaid: boolean;
    pendingPaymentId: string | number;
    active: boolean;
    email?: string;
    isExternalPaid?: boolean;
  };
  wishPurchased?: IWish;
}

export interface IActiveGroupMoneyboxProps {
  groupId: string;
  currentUserId: string;
  amountSortType?: TAmountSortKeys;
  activeGroup: IGroupInfo;
  memberList: IGroupMember[];
  moneybox: IGroupMoneybox;
}

export interface IGroupInfo {
  id: string;
  name: string;
  isCurrentUserAdmin: boolean;
  wishPurchased?: IWish;
  isUsedMoneybox?: boolean;
  protagonist: {
    id: string;
    name: string;
    nickname: string;
    userPhoto: string;
  };
  admin: {
    id: string;
    name: string;
    nickname: string;
    userPhoto: string;
    amountPaid: number;
    hasPaid: boolean;
    pendingPaymentId: string | number;
    active: boolean;
    email?: string;
    isExternalPaid?: boolean;
  };
  photo?: string;
  dates: {
    amountsPollLimitDate?: Date;
    payLimitDate: Date;
    buyLimitDate: Date;
    deliveryLimitDate: Date;
  };
}

export interface IGroupMember {
  id: string;
  name: string;
  nickname: string;
  userPhoto: string;
  hasPaid: boolean;
  pendingPaymentId: string;
  active: boolean;
  isExternalPaid?: boolean;
  amountPaid: number;
  isExternal?: boolean;
}

export interface IGroupWish {
  wish: IWish;
  likes: number;
  isLiked: boolean;
}

export enum EAmountSortKeys {
  AMOUNT = 'amount',
  PAID = 'paid',
  LIKES = 'likes',
}

export type TAmountSortKeys =
  | EAmountSortKeys.AMOUNT
  | EAmountSortKeys.PAID
  | EAmountSortKeys.LIKES;

export interface IAmount {
  id: string;
  feeType: TFeeType;
  [EAmountSortKeys.AMOUNT]: number;
  [EAmountSortKeys.PAID]: number;
  [EAmountSortKeys.LIKES]: number;
  isLiked: boolean;
  isPaid: boolean;
}

export interface IGroupMoneybox {
  amountsPollLimitDate: string;
  payLimitDate: string;
  amount: number;
  amountReturnType: TRefound;
  amountRaisedByLiliApp: number;
  amountRaised: number;
  feeType: TFeeType;
}

export enum EDatesKeys {
  payLimitDate = 'payLimitDate',
  buyLimitDate = 'buyLimitDate',
  deliveryLimitDate = 'deliveryLimitDate',
}

export type TDatesKeys =
  | EDatesKeys.payLimitDate
  | EDatesKeys.buyLimitDate
  | EDatesKeys.deliveryLimitDate;

export enum EAdminRefoundTypes {
  bizum = 'bizum',
  iban = 'iban',
}

export type TAdminRefoundTypes =
  | EAdminRefoundTypes.bizum
  | EAdminRefoundTypes.iban;

export interface IGroupWizardMember {
  id: string;
  userId: string;
  nickname: string;
  name: string;
  photoUrl: string;
  email: string;
  isAdmin: boolean;
  isActive: boolean;
  isContact: boolean;
  isExternal: boolean;
  hasPaid: boolean;
  isExternalPaid: boolean;
  amountPaid: number;
}

export enum EGroupEmails {
  GIFT_BOUGHT = 13,
  GIFT_DELIVERED = 12,
  GIFT_REMINDER = 11
}

export type TGroupEmail = EGroupEmails;
